import { useState, useEffect, useMemo } from 'react'
import {
  MAX_REPLY_BUDGET,
  MIN_REPLY_BUDGET_SLIDER,
  REPLY_BUDGET_STEP,
} from '@/constants/replyBudget'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
  Spinner,
} from '@/components/ui'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { Switch } from '@/components/ui'
import { Textarea } from '@/components/ui'
import { Slider } from '@/components/ui'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { PersonaToolBindings } from './PersonaToolBindings'
import { PersonaSendableAssetsSection } from './PersonaSendableAssetsSection'
import { PaymentsConfigSection } from './PaymentsConfigSection'
import type { PersonaEdit } from '@/hooks/usePersonas'
import { VoiceIdField } from '@/components/voice-browser/VoiceIdField'
import { STTLanguagePicker } from '@/components/voice/STTLanguagePicker'
import type { SpeechMode } from '@/utils/voiceDelivery'
import { defaultVoiceConfigs, useSettingsStore } from '@/stores'
import { useModels, useFeature } from '@/hooks'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'
import { usePermission } from '@/hooks/usePermission'
import { useFeatureStore } from '@/stores/featureStore'
import { RagServerUrlField } from './RagServerUrlField'
import { usePronunciationDictionaries } from '@/hooks/usePronunciationDictionaries'
import { useEdgeLocalConfig } from '@/hooks/useEdgeLocalConfig'
import {
  kokoroOption,
  KOKORO_LABEL,
  KOKORO_UNAVAILABLE_HINT,
  KOKORO_UNAVAILABLE_LABEL,
} from './kokoroOption'
import type {
  Persona,
  PresentationMode,
  VoiceProvider,
  AvatarProvider,
  VoiceConfig,
  AvatarConfig,
  AvatarPipelineMode,
  RagConfig,
  PronunciationRule,
  WhatsAppPersonaConfig,
  CallingConfig,
  PaymentConfig,
} from '@/stores'
import {
  MessageSquare,
  Mic,
  Video,
  Sparkles,
  Save,
  Key,
  Cpu,
  Network,
  RefreshCw,
  Edit3,
  AlertCircle,
  Zap,
  BookOpen,
  BookA,
  Plus,
  Trash2,
  Info,
  Phone,
  MessageCircle,
  ShieldAlert,
  Wrench,
  CreditCard,
} from 'lucide-react'

interface PersonaEditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  persona: Persona
  onSave: (data: PersonaEdit) => Promise<void>
  /** When the modal opens, select this tab instead of the default 'mode'.
   *  Used by the Persona Workshop's Advanced section to deep-link a tab. */
  initialTab?: string
}

type FormData = {
  presentationMode: PresentationMode
  voiceConfig: VoiceConfig | null
  avatarConfig: AvatarConfig | null
  ragConfig: RagConfig
  whatsappConfig: WhatsAppPersonaConfig | null
  paymentConfig: PaymentConfig | null
}

const defaultWhatsAppConfig: WhatsAppPersonaConfig = {
  enabled: true,
  autoReply: true,
  voiceResponse: false,
  voiceMessageMode: 'both',
  maxHistoryMessages: 20,
  sessionTimeoutHours: 12,
  contextBridgingEnabled: true,
}

/** Empty rule template */
const emptyRule: PronunciationRule = {
  stringToReplace: '',
  type: 'alias',
  alias: '',
}

/** Sub-component for managing pronunciation rules */
function PronunciationRulesEditor({
  rules,
  onChange,
}: {
  rules: PronunciationRule[]
  onChange: (rules: PronunciationRule[]) => void
}) {
  const [isAdding, setIsAdding] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [draft, setDraft] = useState<PronunciationRule>({ ...emptyRule })
  const [expanded, setExpanded] = useState(rules.length > 0)

  const resetDraft = () => {
    setDraft({ ...emptyRule })
    setIsAdding(false)
    setEditIndex(null)
  }

  const handleAdd = () => {
    if (!draft.stringToReplace.trim()) return
    if (draft.type === 'alias' && !draft.alias?.trim()) return
    if (draft.type === 'phoneme' && !draft.phoneme?.trim()) return

    if (editIndex !== null) {
      const updated = [...rules]
      updated[editIndex] = { ...draft }
      onChange(updated)
    } else {
      onChange([...rules, { ...draft }])
    }
    resetDraft()
  }

  const handleEdit = (index: number) => {
    setDraft({ ...rules[index] })
    setEditIndex(index)
    setIsAdding(true)
    setExpanded(true)
  }

  const handleDelete = (index: number) => {
    onChange(rules.filter((_, i) => i !== index))
    if (editIndex === index) resetDraft()
  }

  const canSaveDraft =
    draft.stringToReplace.trim() &&
    ((draft.type === 'alias' && draft.alias?.trim()) ||
      (draft.type === 'phoneme' && draft.phoneme?.trim()))

  return (
    <div className="pt-3 border-t border-border">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left mb-2"
      >
        <BookA className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Pronunciation Rules</span>
        {rules.length > 0 && (
          <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
            {rules.length}
          </span>
        )}
        <svg
          className={`w-4 h-4 ml-auto transition-transform text-muted-foreground ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="space-y-3">
          {/* Info callout */}
          <div className="flex gap-2 p-2 bg-info/10 rounded-sm text-xs text-info">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Define how specific words should be pronounced. Alias rules work with all models and languages.
              Rules are case-sensitive.
            </span>
          </div>

          {/* Rules list */}
          {rules.length > 0 && (
            <div className="space-y-1">
              {rules.map((rule, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 bg-background rounded-sm border border-border text-sm"
                >
                  <span className="font-medium truncate min-w-0 flex-1" title={rule.stringToReplace}>
                    {rule.stringToReplace}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground shrink-0">
                    {rule.type}
                  </span>
                  <span className="text-muted-foreground truncate min-w-0 flex-1" title={rule.type === 'alias' ? rule.alias : rule.phoneme}>
                    {rule.type === 'alias' ? rule.alias : rule.phoneme}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleEdit(index)}
                    className="p-1 hover:bg-muted-foreground/10 rounded-sm shrink-0"
                    title="Edit rule"
                  >
                    <Edit3 className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(index)}
                    className="p-1 hover:bg-destructive/10 rounded-sm shrink-0"
                    title="Delete rule"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit form */}
          {isAdding ? (
            <div className="space-y-2 p-3 bg-background rounded-sm border border-border">
              {/* Rule type selector */}
              <div className="flex gap-3">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="ruleType"
                    checked={draft.type === 'alias'}
                    onChange={() => setDraft(prev => ({ ...prev, type: 'alias', phoneme: undefined, alphabet: undefined }))}
                    className="accent-primary"
                  />
                  Alias
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="ruleType"
                    checked={draft.type === 'phoneme'}
                    onChange={() => setDraft(prev => ({ ...prev, type: 'phoneme', alias: undefined, alphabet: 'ipa' }))}
                    className="accent-primary"
                  />
                  Phoneme
                  <span className="text-xs text-muted-foreground">(English, flash/turbo models)</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Word / Phrase</label>
                  <Input
                    value={draft.stringToReplace}
                    onChange={(e) => setDraft(prev => ({ ...prev, stringToReplace: e.target.value }))}
                    placeholder="e.g. Neurealities"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    {draft.type === 'alias' ? 'Pronounce as' : 'IPA Phoneme'}
                  </label>
                  {draft.type === 'alias' ? (
                    <Input
                      value={draft.alias || ''}
                      onChange={(e) => setDraft(prev => ({ ...prev, alias: e.target.value }))}
                      placeholder="e.g. Noor-ee-al-ih-tees"
                      className="text-sm"
                    />
                  ) : (
                    <div className="flex gap-1">
                      <Input
                        value={draft.phoneme || ''}
                        onChange={(e) => setDraft(prev => ({ ...prev, phoneme: e.target.value }))}
                        placeholder="e.g. njʊɹˈiːælɪtiːz"
                        className="text-sm flex-1"
                      />
                      <Select
                        value={draft.alphabet || 'ipa'}
                        onValueChange={(v) => setDraft(prev => ({ ...prev, alphabet: v as 'ipa' | 'cmu' }))}
                      >
                        <SelectTrigger className="w-20 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ipa">IPA</SelectItem>
                          <SelectItem value="cmu">CMU</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-1">
                <Button variant="ghost" size="sm" onClick={resetDraft}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleAdd} disabled={!canSaveDraft}>
                  {editIndex !== null ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { setIsAdding(true); setExpanded(true) }}
              className="w-full"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Pronunciation Rule
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/** Sub-component for selecting shared library pronunciation dictionaries */
function PronunciationDictionaryPicker({
  selectedIds,
  hasRules,
  onChange,
}: {
  selectedIds: string[]
  hasRules: boolean
  onChange: (ids: string[]) => void
}) {
  const { data: dictionaries = [], isLoading, isError, isSuccess } = usePronunciationDictionaries()
  const maxSelectable = hasRules ? 2 : 3
  const knownIds = new Set(dictionaries.map((d) => d.id))
  const unavailable = isSuccess ? selectedIds.filter((id) => !knownIds.has(id)) : []

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id))
    } else if (selectedIds.length < maxSelectable) {
      onChange([...selectedIds, id])
    }
  }

  return (
    <div className="space-y-2 pt-2">
      <label className="text-sm font-medium">Pronunciation Dictionaries</label>
      <p className="text-xs text-muted-foreground">
        Shared dictionaries applied after this persona's rules. ElevenLabs
        allows 3 per request{hasRules ? ' — 1 slot is used by the rules above' : ''}.
      </p>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading dictionaries…</p>
      ) : isError ? (
        <p className="text-xs text-destructive">
          Couldn't load the pronunciation dictionary library. Close and reopen
          the editor to retry.
        </p>
      ) : dictionaries.length === 0 && unavailable.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No dictionaries in the library yet. Admins can upload .pls files
          under Admin → Pronunciation.
        </p>
      ) : (
        <div className="space-y-1 rounded-md border border-border p-2">
          {dictionaries.map((d) => {
            const checked = selectedIds.includes(d.id)
            return (
              <label key={d.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  aria-label={d.name}
                  checked={checked}
                  disabled={!checked && selectedIds.length >= maxSelectable}
                  onChange={() => toggle(d.id)}
                />
                <span>{d.name}</span>
                <span className="text-xs text-muted-foreground">
                  ({d.rules_count} rules)
                </span>
                {d.has_phonemes && (
                  <span className="text-xs text-amber-600">phoneme rules — model-dependent</span>
                )}
              </label>
            )
          })}
        </div>
      )}
      {unavailable.map((id) => (
        <div key={id} className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Selected dictionary unavailable (archived or removed)</span>
          <button
            type="button"
            aria-label={`remove ${id}`}
            className="underline"
            onClick={() => onChange(selectedIds.filter((x) => x !== id))}
          >
            remove
          </button>
        </div>
      ))}
    </div>
  )
}

type CallingMessageMode = 'default' | 'custom' | 'off'

/** SA-182 tri-state for one spoken calling message: Default (inherit the
 * deployment's language-pack copy; persists nothing) / Custom (persona
 * text) / Off (persists `*Enabled: false`; any custom text is retained
 * so toggling back is lossless). */
function CallingMessageControl({
  label,
  help,
  message,
  enabled,
  onChange,
}: {
  label: string
  help: string
  message: string | undefined
  enabled: boolean | undefined
  onChange: (patch: { message?: string; enabled?: boolean }) => void
}) {
  const mode: CallingMessageMode =
    enabled === false ? 'off' : message !== undefined ? 'custom' : 'default'
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <select
          value={mode}
          aria-label={`${label} mode`}
          onChange={(e) => {
            const next = e.target.value as CallingMessageMode
            if (next === 'default') onChange({ message: undefined, enabled: undefined })
            else if (next === 'custom') onChange({ message: message ?? '', enabled: undefined })
            // Off retains any current custom text so Off -> Custom is lossless.
            else onChange({ enabled: false, message })
          }}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        >
          <option value="default">Deployment default</option>
          <option value="custom">Custom</option>
          <option value="off">Off</option>
        </select>
        {mode === 'custom' && (
          <Input
            value={message ?? ''}
            onChange={(e) => onChange({ message: e.target.value })}
            placeholder="Custom message"
            aria-label={`${label} text`}
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{help}</p>
    </div>
  )
}

function seedForm(persona: Persona, showVoice: boolean, showAvatars: boolean): FormData {
  // If the persona's mode is disabled in this deployment, fall back to chat in
  // the UI (the stored value is preserved in the DB for re-enabling).
  let effectiveMode = persona.presentationMode
  if (effectiveMode === 'voice' && !showVoice) effectiveMode = 'chat'
  if (effectiveMode === 'avatar' && !showAvatars) effectiveMode = 'chat'
  return {
    presentationMode: effectiveMode,
    voiceConfig: persona.voiceConfig || null,
    avatarConfig: persona.avatarConfig || null,
    ragConfig: persona.ragConfig,
    whatsappConfig: persona.whatsappConfig || null,
    paymentConfig: persona.paymentConfig ?? null,
  }
}

export function PersonaEditor({
  open,
  onOpenChange,
  persona,
  onSave,
  initialTab,
}: PersonaEditorProps) {
  const showVoice = useFeature('voice')
  const showAvatars = useFeature('avatars')
  const showWhatsApp = useFeature('whatsapp')
  const showToolFramework = useFeature('tool_framework')
  const { localTts } = useEdgeLocalConfig()
  const kokoro = kokoroOption(localTts, persona.voiceConfig?.provider)

  const [formData, setFormData] = useState<FormData>(() => seedForm(persona, showVoice, showAvatars))
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [useCustomModel, setUseCustomModel] = useState(false)
  const [activeTab, setActiveTab] = useState('mode')

  // SA-182: section visibility is pure UI state — opening the calling
  // section must not materialize any config.
  const [callingOpen, setCallingOpen] = useState(false)

  const { connections } = useSettingsStore()
  const hasElevenLabsKey = useSettingsStore((s) => s.hasElevenLabsKey)
  const ragProxyBase = useRagProxyBase()
  const externalAliases = useFeatureStore((s) => s.ragExternalAliases)
  const canOverrideServerUrl = usePermission('personas:override-server-url')

  // Determine effective server URL for model fetching.
  // Priority: persona override → deployment-level RAG_PROXY_BASE.
  const effectiveServerUrl = useMemo(() => {
    return formData.ragConfig.serverUrl || ragProxyBase
  }, [formData.ragConfig.serverUrl, ragProxyBase])

  // Fetch available models from the RAG server
  // Only auto-fetches once on mount with the initial URL
  const {
    models,
    isLoading: isLoadingModels,
    error: modelsError,
    fetchModels,
    lastFetchedUrl,
  } = useModels({
    initialServerUrl: persona.ragConfig.serverUrl || ragProxyBase,
    initialApiKey: persona.ragConfig.apiKey || connections.ragApiKey,
    autoFetch: open, // Only fetch when modal is open
  })

  // Check if the current URL differs from what we last fetched
  const urlChanged = lastFetchedUrl !== null && lastFetchedUrl !== effectiveServerUrl

  // Reset form when modal opens/closes or persona changes
  // Note: Do NOT include `models` in deps - that would reset form when models refresh
  useEffect(() => {
    if (open) {
      setFormData(seedForm(persona, showVoice, showAvatars))
      setUseCustomModel(false)
      setErrors({})
      setActiveTab(initialTab ?? 'mode')
    }
  }, [open, persona, initialTab])

  // Check if we need custom model mode when models are loaded
  // This runs separately so it doesn't reset the entire form
  useEffect(() => {
    if (!open || !persona.ragConfig.model || models.length === 0) return

    const modelExistsInList = models.some(m => m.id === persona.ragConfig.model)
    if (!modelExistsInList) {
      setUseCustomModel(true)
    }
  }, [open, persona.ragConfig.model, models])

  const handlePresentationModeChange = (mode: PresentationMode) => {
    setFormData((prev) => ({
      ...prev,
      presentationMode: mode,
      // Initialize configs when switching modes
      voiceConfig:
        mode === 'voice' || mode === 'avatar'
          ? prev.voiceConfig || {
              ...defaultVoiceConfigs.elevenlabs,
              voiceId: '',
            } as VoiceConfig
          : null,
      avatarConfig:
        mode === 'avatar'
          ? prev.avatarConfig || { provider: 'tavus' as AvatarProvider }
          : null,
    }))
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (formData.presentationMode === 'voice' || formData.presentationMode === 'avatar') {
      if (!formData.voiceConfig?.voiceId) {
        newErrors.voiceId = 'Voice ID is required for voice/avatar modes'
      }
    }

    if (formData.presentationMode === 'avatar') {
      if (formData.avatarConfig?.provider === 'tavus' && !formData.avatarConfig?.replicaId) {
        newErrors.replicaId = 'Replica ID is required for Tavus avatar'
      }
      if (formData.avatarConfig?.provider === 'anam' && !formData.avatarConfig?.anamPersonaId && !formData.avatarConfig?.anamAvatarId) {
        newErrors.anamAvatarId = 'Avatar ID or Persona ID is required for Anam'
      }
    }

    setErrors(newErrors)

    // Switch to the tab containing the first error so user can see it
    if (Object.keys(newErrors).length > 0) {
      if (newErrors.voiceId || newErrors.replicaId || newErrors.anamAvatarId) {
        setActiveTab('mode')
      }
    }

    return Object.keys(newErrors).length === 0
  }

  // SA-182: merge a patch into callingConfig, dropping undefined values
  // so cleared fields return to "inherit"; an empty config disappears.
  const updateCallingConfig = (patch: Partial<CallingConfig>) => {
    setFormData((prev) => {
      const merged: Record<string, unknown> = {
        ...(prev.whatsappConfig!.callingConfig ?? {}),
        ...patch,
      }
      for (const key of Object.keys(merged)) {
        if (merged[key] === undefined) delete merged[key]
      }
      return {
        ...prev,
        whatsappConfig: {
          ...prev.whatsappConfig!,
          callingConfig: Object.keys(merged).length
            ? (merged as CallingConfig)
            : undefined,
        },
      }
    })
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsSaving(true)
    try {
      await onSave({
        presentationMode: formData.presentationMode,
        voiceConfig: formData.voiceConfig || undefined,
        avatarConfig: formData.avatarConfig || undefined,
        ragConfig: formData.ragConfig,
        // Off is null, sent only for a config this editor loaded: one the
        // API withheld (whatsapp_config when the flag is off) is left out,
        // never cleared (SA-1472).
        whatsappConfig: formData.whatsappConfig || (persona.whatsappConfig ? null : undefined),
        paymentConfig: formData.paymentConfig ?? (persona.paymentConfig ? null : undefined),
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save persona:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="xl">
        <ModalHeader>
          <ModalTitle>Persona Settings</ModalTitle>
          <ModalDescription>
            Voice, AI model, channels, and payment settings for this persona.
          </ModalDescription>
        </ModalHeader>

        <ModalBody>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="mode">
                <MessageSquare className="w-4 h-4 mr-2" />
                Presentation
              </TabsTrigger>
              <TabsTrigger value="rag">
                <Sparkles className="w-4 h-4 mr-2" />
                AI Settings
              </TabsTrigger>
              {showWhatsApp && (
                <TabsTrigger value="channels">
                  <Phone className="w-4 h-4 mr-2" />
                  Channels
                </TabsTrigger>
              )}
              {showToolFramework && (
                <TabsTrigger value="tools">
                  <Wrench className="w-4 h-4 mr-2" />
                  Tools
                </TabsTrigger>
              )}
              <TabsTrigger value="payments">
                <CreditCard className="w-4 h-4 mr-2" />
                Payments
              </TabsTrigger>
            </TabsList>

            {/* Presentation Mode Tab */}
            <TabsContent value="mode" className="space-y-6">
              {/* Mode Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Presentation Mode
                </label>
                <div className={`grid gap-3 ${
                  [true, showVoice, showAvatars].filter(Boolean).length === 3 ? 'grid-cols-3' :
                  [true, showVoice, showAvatars].filter(Boolean).length === 2 ? 'grid-cols-2' : 'grid-cols-1'
                }`}>
                  {[
                    { id: 'chat' as const, icon: MessageSquare, label: 'Chat', desc: 'Text only' },
                    ...(showVoice ? [{ id: 'voice' as const, icon: Mic, label: 'Voice', desc: 'Voice conversation' }] : []),
                    ...(showAvatars ? [{ id: 'avatar' as const, icon: Video, label: 'Avatar', desc: 'Video avatar' }] : []),
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => handlePresentationModeChange(mode.id)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.presentationMode === mode.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <mode.icon
                        className={`w-6 h-6 mx-auto mb-2 ${
                          formData.presentationMode === mode.id
                            ? 'text-primary'
                            : 'text-muted-foreground'
                        }`}
                      />
                      <div className="text-sm font-medium">{mode.label}</div>
                      <div className="text-xs text-muted-foreground">{mode.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Voice Configuration */}
              {(formData.presentationMode === 'voice' ||
                formData.presentationMode === 'avatar') && (
                <div className="space-y-4 p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Mic className="w-4 h-4 text-primary" />
                    <span className="font-medium">Voice Configuration</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Provider</label>
                      <Select
                        value={formData.voiceConfig?.provider || 'elevenlabs'}
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            voiceConfig: {
                              ...defaultVoiceConfigs[value as VoiceProvider],
                              voiceId: prev.voiceConfig?.voiceId || '',
                            } as VoiceConfig,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                          <SelectItem value="azure">Azure Speech</SelectItem>
                          {kokoro !== 'hidden' && (
                            <SelectItem value="kokoro" disabled={kokoro === 'unavailable'}>
                              {kokoro === 'unavailable' ? KOKORO_UNAVAILABLE_LABEL : KOKORO_LABEL}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {kokoro === 'unavailable' && formData.voiceConfig?.provider === 'kokoro' && (
                        <p className="text-xs text-muted-foreground">{KOKORO_UNAVAILABLE_HINT}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Voice *</label>
                      <VoiceIdField
                        provider={formData.voiceConfig?.provider || 'elevenlabs'}
                        voiceId={formData.voiceConfig?.voiceId || ''}
                        voiceName={formData.voiceConfig?.voiceName}
                        voicePreviewUrl={formData.voiceConfig?.voicePreviewUrl}
                        onChange={(voiceId, voiceName, voicePreviewUrl, supportedModels) =>
                          setFormData((prev) => {
                            const newModels = supportedModels ?? prev.voiceConfig?.supportedModels
                            // Auto-select model if current modelId isn't supported by new voice
                            let modelId = prev.voiceConfig?.modelId
                            if (newModels?.length && modelId && !newModels.includes(modelId)) {
                              modelId = newModels[0]
                            }
                            return {
                              ...prev,
                              voiceConfig: {
                                ...prev.voiceConfig!,
                                voiceId,
                                voiceName,
                                voicePreviewUrl,
                                supportedModels: newModels,
                                modelId,
                              },
                            }
                          })
                        }
                        hasApiKey={
                          formData.voiceConfig?.provider === 'kokoro' ||
                          (formData.voiceConfig?.provider === 'elevenlabs' && hasElevenLabsKey) ||
                          false
                        }
                        error={errors.voiceId}
                      />
                    </div>
                  </div>

                  {/* STT Language Override (persona-level) */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Speech Recognition Language
                    </label>
                    <STTLanguagePicker
                      value={formData.voiceConfig?.sttLanguage ?? null}
                      onChange={(code) =>
                        setFormData((prev) => ({
                          ...prev,
                          voiceConfig: {
                            ...prev.voiceConfig!,
                            sttLanguage: code ?? undefined,
                          },
                        }))
                      }
                      nullSentinel="use-account-default"
                    />
                    <p className="text-xs text-muted-foreground">
                      Override the user's default speech-recognition language for this persona.
                      Leave as "Use account default" to inherit from the user's voice settings.
                    </p>
                  </div>

                  {/* Voice delivery style (display/speech split) */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      Voice Delivery Style
                    </label>
                    <Select
                      value={formData.voiceConfig?.speechMode || 'verbatim'}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          voiceConfig: {
                            ...prev.voiceConfig!,
                            speechMode: value as SpeechMode,
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="verbatim">
                          Speak the answer as written (default)
                        </SelectItem>
                        <SelectItem value="gloss">
                          Spoken summary — refer to screen for details
                        </SelectItem>
                        <SelectItem value="full">
                          Speak everything, rewritten conversationally
                        </SelectItem>
                        <SelectItem value="adaptive">
                          Adaptive — full for short answers, summary for long
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      How the voice reads structured answers when voice or avatar
                      mode is on. The on-screen text always keeps its full
                      structure; non-default styles ask the persona to open with a
                      short conversational spoken version instead of reading the
                      structure aloud.
                    </p>
                  </div>

                  {formData.voiceConfig?.provider === 'elevenlabs' && (
                    <>
                      {/* TTS Model Selection */}
                      <div className="space-y-2 pt-2">
                        <label className="text-sm font-medium">TTS Model</label>
                        <Select
                          value={formData.voiceConfig?.modelId || 'eleven_flash_v2_5'}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              voiceConfig: {
                                ...prev.voiceConfig!,
                                modelId: value,
                              },
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(formData.voiceConfig?.supportedModels?.length
                              ? formData.voiceConfig.supportedModels
                              : ['eleven_flash_v2_5', 'eleven_turbo_v2_5', 'eleven_multilingual_v2']
                            ).map((modelId) => {
                              const names: Record<string, string> = {
                                eleven_multilingual_v2: 'Multilingual v2',
                                eleven_multilingual_v1: 'Multilingual v1',
                                eleven_monolingual_v1: 'English v1',
                                eleven_turbo_v2: 'Turbo v2',
                                eleven_turbo_v2_5: 'Turbo v2.5',
                                eleven_flash_v2: 'Flash v2',
                                eleven_flash_v2_5: 'Flash v2.5',
                              }
                              return (
                                <SelectItem key={modelId} value={modelId}>
                                  {names[modelId] ?? modelId}
                                </SelectItem>
                              )
                            })}
                          </SelectContent>
                        </Select>
                        {!formData.voiceConfig?.supportedModels?.length && formData.voiceConfig?.voiceName && (
                          <p className="text-[11px] text-muted-foreground">
                            Re-select the voice from Browse to see which models it supports.
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Stability: {formData.voiceConfig?.stability ?? 0.5}
                          </label>
                          <Slider
                            value={[formData.voiceConfig?.stability ?? 0.5]}
                            min={0}
                            max={1}
                            step={0.05}
                            onValueChange={([value]) =>
                              setFormData((prev) => ({
                                ...prev,
                                voiceConfig: {
                                  ...prev.voiceConfig!,
                                  stability: value,
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Similarity: {formData.voiceConfig?.similarityBoost ?? 0.75}
                          </label>
                          <Slider
                            value={[formData.voiceConfig?.similarityBoost ?? 0.75]}
                            min={0}
                            max={1}
                            step={0.05}
                            onValueChange={([value]) =>
                              setFormData((prev) => ({
                                ...prev,
                                voiceConfig: {
                                  ...prev.voiceConfig!,
                                  similarityBoost: value,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>

                      {/* Pronunciation Rules */}
                      <PronunciationRulesEditor
                        rules={formData.voiceConfig?.pronunciationRules || []}
                        onChange={(rules) =>
                          setFormData((prev) => {
                            const ids = prev.voiceConfig?.pronunciationDictionaryIds
                            return {
                              ...prev,
                              voiceConfig: {
                                ...prev.voiceConfig!,
                                pronunciationRules: rules.length > 0 ? rules : undefined,
                                // Invalidate cached dictionary when rules change
                                pronunciationDictionary: undefined,
                                pronunciationDictionaryIds:
                                  rules.length > 0 && ids && ids.length > 2
                                    ? ids.slice(0, 2)
                                    : ids,
                              },
                            }
                          })
                        }
                      />

                      <PronunciationDictionaryPicker
                        selectedIds={formData.voiceConfig?.pronunciationDictionaryIds || []}
                        hasRules={(formData.voiceConfig?.pronunciationRules?.length ?? 0) > 0}
                        onChange={(ids) =>
                          setFormData((prev) => ({
                            ...prev,
                            voiceConfig: {
                              ...prev.voiceConfig!,
                              pronunciationDictionaryIds: ids.length > 0 ? ids : undefined,
                            },
                          }))
                        }
                      />
                    </>
                  )}
                </div>
              )}

              {/* Avatar Configuration */}
              {formData.presentationMode === 'avatar' && (
                <div className="space-y-4 p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Video className="w-4 h-4 text-primary" />
                    <span className="font-medium">Avatar Configuration</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Provider</label>
                      <Select
                        value={formData.avatarConfig?.provider || 'tavus'}
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            avatarConfig: {
                              provider: value as AvatarProvider,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tavus">Tavus</SelectItem>
                          <SelectItem value="anam">Anam</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.avatarConfig?.provider === 'tavus' && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Replica ID *</label>
                        <Input
                          value={formData.avatarConfig?.replicaId || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              avatarConfig: {
                                ...prev.avatarConfig!,
                                replicaId: e.target.value,
                              },
                            }))
                          }
                          placeholder="Enter Tavus replica ID"
                          error={errors.replicaId}
                        />
                      </div>
                    )}
                  </div>

                  {/* Anam Configuration */}
                  {formData.avatarConfig?.provider === 'anam' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Anam Persona ID</label>
                        <Input
                          value={formData.avatarConfig?.anamPersonaId || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              avatarConfig: {
                                ...prev.avatarConfig!,
                                anamPersonaId: e.target.value || undefined,
                              },
                            }))
                          }
                          placeholder="Pre-configured persona UUID (optional)"
                        />
                        <p className="text-xs text-muted-foreground">
                          If set, overrides avatar/voice/LLM fields below.
                        </p>
                      </div>

                      {!formData.avatarConfig?.anamPersonaId && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Avatar ID *</label>
                            <Input
                              value={formData.avatarConfig?.anamAvatarId || ''}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  avatarConfig: {
                                    ...prev.avatarConfig!,
                                    anamAvatarId: e.target.value,
                                  },
                                }))
                              }
                              placeholder="Anam avatar UUID"
                              error={errors.anamAvatarId}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Voice ID</label>
                            <Input
                              value={formData.avatarConfig?.anamVoiceId || ''}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  avatarConfig: {
                                    ...prev.avatarConfig!,
                                    anamVoiceId: e.target.value || undefined,
                                  },
                                }))
                              }
                              placeholder="Anam voice UUID"
                            />
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Language</label>
                          <STTLanguagePicker
                            value={formData.avatarConfig?.anamLanguageCode ?? null}
                            onChange={(code) =>
                              setFormData((prev) => ({
                                ...prev,
                                avatarConfig: {
                                  ...prev.avatarConfig!,
                                  anamLanguageCode: code ?? undefined,
                                },
                              }))
                            }
                            nullSentinel="use-account-default"
                          />
                          <p className="text-xs text-muted-foreground">
                            Optional. When set, Anam uses this language code for the avatar's voice.
                          </p>
                        </div>
                        <div className="space-y-2 flex flex-col justify-end gap-2">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.avatarConfig?.anamEnableRecording ?? true}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  avatarConfig: {
                                    ...prev.avatarConfig!,
                                    anamEnableRecording: e.target.checked,
                                  },
                                }))
                              }
                              className="rounded-sm border-border"
                            />
                            Session Recording
                          </label>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.avatarConfig?.anamEnableClientTools ?? false}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  avatarConfig: {
                                    ...prev.avatarConfig!,
                                    anamEnableClientTools: e.target.checked,
                                  },
                                }))
                              }
                              className="rounded-sm border-border"
                            />
                            Client Tools
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pipeline Mode */}
                  {(formData.avatarConfig?.provider === 'tavus' || formData.avatarConfig?.provider === 'anam') && (
                    <div className="space-y-3 pt-2">
                      <label className="text-sm font-medium text-foreground">
                        Avatar Intelligence
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {(formData.avatarConfig?.provider === 'tavus'
                          ? [
                              {
                                id: 'tavus_ai' as AvatarPipelineMode,
                                icon: Zap,
                                label: 'Tavus AI',
                                desc: 'Built-in conversational AI, fast and low-latency',
                              },
                              {
                                id: 'expert_rag' as AvatarPipelineMode,
                                icon: BookOpen,
                                label: 'Expert RAG',
                                desc: 'Uses your knowledge base, shows sources',
                              },
                            ]
                          : [
                              {
                                id: 'anam_ai' as AvatarPipelineMode,
                                icon: Zap,
                                label: 'Anam AI',
                                desc: "Anam's built-in conversational AI",
                              },
                              {
                                id: 'expert_rag' as AvatarPipelineMode,
                                icon: BookOpen,
                                label: 'Expert RAG',
                                desc: 'Uses your knowledge base, shows sources',
                              },
                            ]
                        ).map((mode) => {
                          const defaultMode = formData.avatarConfig?.provider === 'tavus' ? 'tavus_ai' : 'anam_ai'
                          return (
                            <button
                              key={mode.id}
                              type="button"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  avatarConfig: {
                                    ...prev.avatarConfig!,
                                    pipelineMode: mode.id,
                                  },
                                }))
                              }
                              className={`p-3 rounded-lg border-2 transition-all text-left ${
                                (formData.avatarConfig?.pipelineMode || defaultMode) === mode.id
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border hover:border-muted-foreground/40'
                              }`}
                            >
                              <mode.icon
                                className={`w-5 h-5 mb-1 ${
                                  (formData.avatarConfig?.pipelineMode || defaultMode) === mode.id
                                    ? 'text-primary'
                                    : 'text-muted-foreground'
                                }`}
                              />
                              <div className="text-sm font-medium">{mode.label}</div>
                              <div className="text-xs text-muted-foreground mt-0.5">{mode.desc}</div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Anam AI mode — LLM and system prompt */}
                  {formData.avatarConfig?.provider === 'anam' &&
                    (formData.avatarConfig?.pipelineMode || 'anam_ai') === 'anam_ai' &&
                    !formData.avatarConfig?.anamPersonaId && (
                    <div className="space-y-4 pt-2">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">LLM ID</label>
                        <Input
                          value={formData.avatarConfig?.anamLlmId || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              avatarConfig: {
                                ...prev.avatarConfig!,
                                anamLlmId: e.target.value || undefined,
                              },
                            }))
                          }
                          placeholder="Anam LLM UUID (uses default if empty)"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Anam System Prompt</label>
                        <Textarea
                          value={formData.avatarConfig?.anamSystemPrompt || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              avatarConfig: {
                                ...prev.avatarConfig!,
                                anamSystemPrompt: e.target.value || undefined,
                              },
                            }))
                          }
                          placeholder="System prompt for Anam's built-in LLM"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                          Only used in Anam AI mode. Expert RAG mode uses the system prompt from the AI Settings tab.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* AI Settings Tab */}
            <TabsContent value="rag" className="space-y-4">
              {/* RAG Server URL — structured override (Phase 5 of rag-deployment-config) */}
              <RagServerUrlField
                value={formData.ragConfig.serverUrl}
                onChange={(serverUrl) =>
                  setFormData((prev) => ({
                    ...prev,
                    ragConfig: { ...prev.ragConfig, serverUrl },
                  }))
                }
                deploymentBase={ragProxyBase}
                aliases={externalAliases}
                canSetCustomUrl={canOverrideServerUrl}
              />

              {/* RAG Server API Key */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  RAG Server API Key
                </label>
                <Input
                  type="password"
                  value={formData.ragConfig.apiKey || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ragConfig: {
                        ...prev.ragConfig,
                        apiKey: e.target.value || undefined,
                      },
                    }))
                  }
                  placeholder="(uses your Settings or credential-set key if empty)"
                />
                <p className="text-xs text-muted-foreground">
                  Optional: overrides the RAG server API key for this persona. Leave empty to use your key from Settings → Connections or your assigned credential set.
                </p>
              </div>

              {/* Graph ID (multi-graph HybridRAG routing) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Network className="w-4 h-4" />
                  Graph ID
                </label>
                <Input
                  value={formData.ragConfig.graphId || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ragConfig: {
                        ...prev.ragConfig,
                        graphId: e.target.value || undefined,
                      },
                    }))
                  }
                  placeholder="e.g. mayo-cardiology (uses server default if empty)"
                  pattern="^[a-z](?:[a-z0-9-]{0,62}[a-z0-9])?$"
                  maxLength={64}
                />
                <p className="text-xs text-muted-foreground">
                  Optional: Selects a specific graph on a multi-graph HybridRAG instance. Leave empty to use the server's default graph. Lowercase letters, digits, and hyphens; must start with a letter; max 64 chars.
                </p>
              </div>

              {/* Model Selection */}
              <div className="space-y-3 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Cpu className="w-4 h-4" />
                    Model
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fetchModels(effectiveServerUrl, formData.ragConfig.apiKey || connections.ragApiKey || '')}
                      disabled={isLoadingModels}
                      className={`p-1.5 rounded-md transition-colors disabled:opacity-50 ${
                        urlChanged
                          ? 'bg-warning/10 hover:bg-warning/20'
                          : 'hover:bg-muted-foreground/10'
                      }`}
                      title={urlChanged ? 'Click to fetch models from new URL' : 'Refresh model list'}
                    >
                      <RefreshCw className={`w-4 h-4 ${urlChanged ? 'text-warning' : 'text-muted-foreground'} ${isLoadingModels ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUseCustomModel(!useCustomModel)
                        if (useCustomModel) {
                          // Switching back to dropdown - clear model if it doesn't exist in list
                          const currentModel = formData.ragConfig.model
                          if (currentModel && models.length > 0 && !models.find(m => m.id === currentModel)) {
                            setFormData((prev) => ({
                              ...prev,
                              ragConfig: { ...prev.ragConfig, model: undefined },
                            }))
                          }
                        }
                      }}
                      className={`p-1.5 rounded-md transition-colors ${
                        useCustomModel
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted-foreground/10 text-muted-foreground'
                      }`}
                      title={useCustomModel ? 'Use model dropdown' : 'Enter custom model'}
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Loading state */}
                {isLoadingModels && !useCustomModel && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner size="sm" />
                    Loading available models...
                  </div>
                )}

                {/* Error state */}
                {modelsError && !useCustomModel && (
                  <div className="flex items-center gap-2 text-sm text-warning-strong">
                    <AlertCircle className="w-4 h-4" />
                    <span>Could not fetch models: {modelsError}</span>
                  </div>
                )}

                {/* Model selector or custom input */}
                {useCustomModel ? (
                  <div className="space-y-2">
                    <Input
                      value={formData.ragConfig.model || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          ragConfig: {
                            ...prev.ragConfig,
                            model: e.target.value || undefined,
                          },
                        }))
                      }
                      placeholder="e.g., gpt-4o, claude-3-opus, llama-3.1-70b"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the model identifier exactly as supported by your RAG server.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Select
                      value={formData.ragConfig.model || '__default__'}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          ragConfig: {
                            ...prev.ragConfig,
                            model: value === '__default__' ? undefined : value,
                          },
                        }))
                      }
                      disabled={isLoadingModels || models.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={
                          isLoadingModels
                            ? 'Loading models...'
                            : models.length === 0
                              ? 'No models available'
                              : 'Select a model'
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">
                          <span className="text-muted-foreground">Use server default</span>
                        </SelectItem>
                        {models.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex flex-col">
                              <span>{model.id}</span>
                              {model.owned_by && (
                                <span className="text-xs text-muted-foreground">{model.owned_by}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {urlChanged ? (
                        <span className="text-warning-strong">
                          URL changed - click refresh to load models from new server
                        </span>
                      ) : models.length > 0 ? (
                        `${models.length} model${models.length === 1 ? '' : 's'} available`
                      ) : (
                        'Click refresh or enter a custom model name'
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Raw system prompt editing was removed from this modal (D1/D2,
                  Settings reframe). The Workshop's composed-prompt inspector
                  is the canonical read/edit surface; this modal's save still
                  passes ragConfig (incl. systemPrompt) through byte-for-byte
                  so a Settings save never wipes the Workshop-synced prompt. */}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Temperature: {formData.ragConfig.temperature}
                  </label>
                  <Slider
                    value={[formData.ragConfig.temperature]}
                    min={0}
                    max={2}
                    step={0.1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({
                        ...prev,
                        ragConfig: { ...prev.ragConfig, temperature: value },
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower = more focused, Higher = more creative
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Reply budget (tokens): {formData.ragConfig.maxTokens}
                  </label>
                  <Slider
                    value={[formData.ragConfig.maxTokens]}
                    min={MIN_REPLY_BUDGET_SLIDER}
                    max={MAX_REPLY_BUDGET}
                    step={REPLY_BUDGET_STEP}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({
                        ...prev,
                        ragConfig: { ...prev.ragConfig, maxTokens: value },
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    The most one reply may spend. Shape length in the persona's instructions.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Top K Sources: {formData.ragConfig.topK}
                  </label>
                  <Slider
                    value={[formData.ragConfig.topK]}
                    min={1}
                    max={20}
                    step={1}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({
                        ...prev,
                        ragConfig: { ...prev.ragConfig, topK: value },
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Number of sources to retrieve
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Score Threshold: {formData.ragConfig.scoreThreshold}
                  </label>
                  <Slider
                    value={[formData.ragConfig.scoreThreshold]}
                    min={0}
                    max={1}
                    step={0.05}
                    onValueChange={([value]) =>
                      setFormData((prev) => ({
                        ...prev,
                        ragConfig: { ...prev.ragConfig, scoreThreshold: value },
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum relevance score for sources
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Channels Tab — only when whatsapp feature is enabled */}
            {showWhatsApp && <TabsContent value="channels" className="space-y-4">
              {/* WhatsApp Section */}
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* WhatsApp channel identity green — rule-5 leave (A1 precedent) */}
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    <span className="font-medium">WhatsApp</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm text-muted-foreground">
                      {formData.whatsappConfig ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={!!formData.whatsappConfig}
                      aria-label="Enable WhatsApp"
                      onCheckedChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          whatsappConfig: prev.whatsappConfig
                            ? null
                            : { ...defaultWhatsAppConfig },
                        }))
                      }
                    />
                  </label>
                </div>

                {!formData.whatsappConfig && (
                  <p className="text-sm text-muted-foreground">
                    Enable WhatsApp to allow this persona to receive and respond to WhatsApp messages.
                  </p>
                )}

                {formData.whatsappConfig && (
                  <div className="space-y-4 pt-2">
                    {/* Greeting Message */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Greeting Message
                      </label>
                      <Textarea
                        value={formData.whatsappConfig.greetingMessage || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              greetingMessage: e.target.value || undefined,
                            },
                          }))
                        }
                        placeholder="Welcome! How can I help you today?"
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground">
                        Sent when a new customer starts a conversation
                      </p>
                    </div>

                    {/* Toggles row */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Auto-Reply */}
                      <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                        <div>
                          <div className="text-sm font-medium">Auto-Reply</div>
                          <div className="text-xs text-muted-foreground">AI responds automatically</div>
                        </div>
                        <Switch
                          size="sm"
                          checked={formData.whatsappConfig.autoReply}
                          aria-label="Auto-Reply"
                          onCheckedChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              whatsappConfig: {
                                ...prev.whatsappConfig!,
                                autoReply: !prev.whatsappConfig!.autoReply,
                              },
                            }))
                          }
                        />
                      </div>

                      {/* Voice Response */}
                      <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                        <div>
                          <div className="text-sm font-medium">Voice Response</div>
                          <div className="text-xs text-muted-foreground">Send audio replies</div>
                        </div>
                        <Switch
                          size="sm"
                          checked={formData.whatsappConfig.voiceResponse}
                          aria-label="Voice Response"
                          onCheckedChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              whatsappConfig: {
                                ...prev.whatsappConfig!,
                                voiceResponse: !prev.whatsappConfig!.voiceResponse,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>

                    {/* Reply mode default (only meaningful when Voice Response is on) */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Default reply delivery</label>
                      <Select
                        value={formData.whatsappConfig.voiceMessageMode ?? 'both'}
                        disabled={!formData.whatsappConfig.voiceResponse}
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              voiceMessageMode: value as 'both' | 'text' | 'voice',
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Both voice and text" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="both">Both voice and text</SelectItem>
                          <SelectItem value="text">Text only</SelectItem>
                          <SelectItem value="voice">Voice notes only</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-xs text-muted-foreground">
                        {formData.whatsappConfig.voiceResponse
                          ? 'Customers can ask to switch this mid-chat.'
                          : 'Enable Voice Response to set a default mode.'}
                      </div>
                    </div>

                    {/* Burst Debounce */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Burst debounce (seconds)
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={
                          formData.whatsappConfig.burstDebounceSeconds ?? ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value
                          const parsed =
                            raw === '' ? undefined : Math.max(0, Number(raw))
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              burstDebounceSeconds:
                                parsed !== undefined && Number.isNaN(parsed)
                                  ? prev.whatsappConfig!.burstDebounceSeconds
                                  : parsed,
                            },
                          }))
                        }}
                        placeholder="Use deployment default"
                        className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Wait this many seconds of silence to combine a rapid burst
                        of customer messages into one reply. Leave blank to use the
                        deployment default; set 0 for instant replies (no
                        coalescing).
                      </p>
                    </div>

                    {/* Business Timezone */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Business timezone (for inventory dates)
                      </label>
                      <input
                        type="text"
                        value={formData.whatsappConfig.timezone || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              timezone: e.target.value || undefined,
                            },
                          }))
                        }
                        placeholder="America/Mexico_City"
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        IANA timezone used when calculating inventory availability dates
                        (e.g. <code>America/Mexico_City</code>, <code>America/New_York</code>).
                        Leave blank to use the server default (UTC).
                      </p>
                    </div>

                    {/* Max History Messages */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Conversation History: {formData.whatsappConfig.maxHistoryMessages} messages
                      </label>
                      <Slider
                        value={[formData.whatsappConfig.maxHistoryMessages]}
                        min={1}
                        max={100}
                        step={1}
                        onValueChange={([value]) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              maxHistoryMessages: value,
                            },
                          }))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Number of recent messages included as context for AI responses
                      </p>
                    </div>

                    {/* Session Timeout */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Session Timeout: {formData.whatsappConfig.sessionTimeoutHours ?? 12} hours
                      </label>
                      <Slider
                        value={[formData.whatsappConfig.sessionTimeoutHours ?? 12]}
                        min={1}
                        max={48}
                        step={1}
                        onValueChange={([value]) =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              sessionTimeoutHours: value,
                            },
                          }))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        After this period of inactivity, new messages start a fresh conversation
                      </p>
                    </div>

                    {/* Context Bridging */}
                    <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
                      <div>
                        <div className="text-sm font-medium">Context Bridging</div>
                        <div className="text-xs text-muted-foreground">
                          Give AI context from previous conversations with returning customers
                        </div>
                      </div>
                      <Switch
                        size="sm"
                        checked={formData.whatsappConfig.contextBridgingEnabled ?? true}
                        aria-label="Context Bridging"
                        onCheckedChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              contextBridgingEnabled: !(prev.whatsappConfig!.contextBridgingEnabled ?? true),
                            },
                          }))
                        }
                      />
                    </div>

                    {/* Escalation Config */}
                    <div className="pt-2 border-t border-border">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            whatsappConfig: {
                              ...prev.whatsappConfig!,
                              escalationConfig: prev.whatsappConfig!.escalationConfig
                                ? undefined
                                : { enabled: true, triggerKeywords: [] },
                            },
                          }))
                        }
                        className="flex items-center gap-2 w-full text-left mb-2"
                      >
                        <ShieldAlert className="w-4 h-4 text-warning" />
                        <span className="text-sm font-medium">Human Escalation</span>
                        {formData.whatsappConfig.escalationConfig?.enabled && (
                          <span className="text-xs bg-warning/10 text-warning-strong px-1.5 py-0.5 rounded-full">
                            Active
                          </span>
                        )}
                        <svg
                          className={`w-4 h-4 ml-auto transition-transform text-muted-foreground ${formData.whatsappConfig.escalationConfig ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {formData.whatsappConfig.escalationConfig && (
                        <div className="space-y-3 pl-6">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">
                              Trigger Keywords
                            </label>
                            <Input
                              value={formData.whatsappConfig.escalationConfig.triggerKeywords?.join(', ') || ''}
                              onChange={(e) => {
                                const keywords = e.target.value
                                  .split(',')
                                  .map(k => k.trim())
                                  .filter(Boolean)
                                setFormData((prev) => ({
                                  ...prev,
                                  whatsappConfig: {
                                    ...prev.whatsappConfig!,
                                    escalationConfig: {
                                      ...prev.whatsappConfig!.escalationConfig!,
                                      triggerKeywords: keywords,
                                    },
                                  },
                                }))
                              }}
                              placeholder="e.g., human, agent, help, speak to someone"
                            />
                            <p className="text-xs text-muted-foreground">
                              Comma-separated keywords that trigger handoff to a human agent
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Voice Calling Config (SA-182: delta persistence —
                        unset fields inherit deployment defaults; opening
                        this section persists nothing) */}
                    <div className="pt-2 border-t border-border">
                      <button
                        type="button"
                        onClick={() => setCallingOpen((o) => !o)}
                        className="flex items-center gap-2 w-full text-left mb-2"
                      >
                        {/* WhatsApp channel identity green — rule-5 leave (A1 precedent) */}
                        <Phone className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium">Voice Calling</span>
                        {formData.whatsappConfig.callingConfig && (
                          <span className="text-xs bg-success/10 text-success px-1.5 py-0.5 rounded-full">
                            Customized
                          </span>
                        )}
                        <svg
                          className={`w-4 h-4 ml-auto transition-transform text-muted-foreground ${callingOpen ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {callingOpen && (
                        <div className="space-y-4 pl-6">
                          <p className="text-xs text-muted-foreground">
                            Whether this deployment answers calls is configured at the
                            deployment level. Fields left on their defaults inherit
                            deployment behavior — only overrides are saved.
                          </p>

                          <CallingMessageControl
                            label="Greeting"
                            help="First thing the AI says when answering. Off answers silently."
                            message={formData.whatsappConfig.callingConfig?.greetingMessage}
                            enabled={formData.whatsappConfig.callingConfig?.greetingEnabled}
                            onChange={({ message, enabled }) =>
                              updateCallingConfig({ greetingMessage: message, greetingEnabled: enabled })
                            }
                          />

                          <CallingMessageControl
                            label="Goodbye"
                            help="Spoken before hanging up after extended silence."
                            message={formData.whatsappConfig.callingConfig?.goodbyeMessage}
                            enabled={formData.whatsappConfig.callingConfig?.goodbyeEnabled}
                            onChange={({ message, enabled }) =>
                              updateCallingConfig({ goodbyeMessage: message, goodbyeEnabled: enabled })
                            }
                          />

                          <CallingMessageControl
                            label="Silence prompt"
                            help="Check-in when the caller goes quiet. Off waits silently until hangup."
                            message={formData.whatsappConfig.callingConfig?.silencePromptMessage}
                            enabled={formData.whatsappConfig.callingConfig?.silencePromptEnabled}
                            onChange={({ message, enabled }) =>
                              updateCallingConfig({ silencePromptMessage: message, silencePromptEnabled: enabled })
                            }
                          />

                          {/* Timers — burst-debounce idiom: blank = default */}
                          <div className="space-y-1">
                            <label className="text-sm font-medium text-foreground">
                              Silence prompt after (seconds)
                            </label>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              value={formData.whatsappConfig.callingConfig?.silencePromptSeconds ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value
                                const parsed = raw === '' ? undefined : Number(raw)
                                if (parsed === undefined || (!Number.isNaN(parsed) && parsed > 0)) {
                                  updateCallingConfig({ silencePromptSeconds: parsed })
                                }
                              }}
                              placeholder="10 (default)"
                              className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-sm font-medium text-foreground">
                              Hang up after silence (seconds)
                            </label>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              value={formData.whatsappConfig.callingConfig?.silenceHangupSeconds ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value
                                const parsed = raw === '' ? undefined : Number(raw)
                                if (parsed === undefined || (!Number.isNaN(parsed) && parsed > 0)) {
                                  updateCallingConfig({ silenceHangupSeconds: parsed })
                                }
                              }}
                              placeholder="30 (default)"
                              className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
                            />
                          </div>

                          {/* Barge-in */}
                          <div className="space-y-1">
                            <label className="text-sm font-medium text-foreground">Allow barge-in</label>
                            <select
                              aria-label="Allow barge-in"
                              value={
                                formData.whatsappConfig.callingConfig?.allowBargeIn === undefined
                                  ? 'default'
                                  : formData.whatsappConfig.callingConfig.allowBargeIn
                                    ? 'on'
                                    : 'off'
                              }
                              onChange={(e) =>
                                updateCallingConfig({
                                  allowBargeIn:
                                    e.target.value === 'default' ? undefined : e.target.value === 'on',
                                })
                              }
                              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                            >
                              <option value="default">Default (on)</option>
                              <option value="on">On</option>
                              <option value="off">Off</option>
                            </select>
                            <p className="text-xs text-muted-foreground">
                              Let the caller interrupt the AI mid-response
                            </p>
                          </div>

                          {/* TTS speed */}
                          <div className="space-y-1">
                            <label className="text-sm font-medium text-foreground">Speech speed</label>
                            <input
                              type="number"
                              min={0.7}
                              max={1.2}
                              step={0.1}
                              value={formData.whatsappConfig.callingConfig?.ttsSpeed ?? ''}
                              onChange={(e) => {
                                // Never reject keystrokes — an in-range midpoint typed
                                // digit-by-digit (e.g. "0" then "0.8") would otherwise
                                // get silently dropped by a bounds check here. Blur
                                // clamps into range instead.
                                const raw = e.target.value
                                const parsed = raw === '' ? undefined : Number(raw)
                                if (parsed === undefined || !Number.isNaN(parsed)) {
                                  updateCallingConfig({ ttsSpeed: parsed })
                                }
                              }}
                              onBlur={() => {
                                const current = formData.whatsappConfig!.callingConfig?.ttsSpeed
                                if (current !== undefined) {
                                  const clamped = Math.min(1.2, Math.max(0.7, current))
                                  if (clamped !== current) updateCallingConfig({ ttsSpeed: clamped })
                                }
                              }}
                              placeholder="Voice speed (default)"
                              className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                              0.7 (slow) to 1.2 (fast). Blank inherits the voice speed setting.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>}

            {showToolFramework && (
              <TabsContent value="tools" className="space-y-4">
                <PersonaToolBindings personaId={persona.id} />
                <PersonaSendableAssetsSection personaId={persona.id} />
              </TabsContent>
            )}

            {/* Payments Tab */}
            <TabsContent value="payments" className="space-y-4">
              <PaymentsConfigSection
                value={formData.paymentConfig}
                onChange={(paymentConfig) =>
                  setFormData((prev) => ({ ...prev, paymentConfig }))
                }
              />
            </TabsContent>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
